import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  MapPin, 
  Clock, 
  Building2, 
  ChevronDown, 
  ChevronUp,
  Heart,
  Users,
  Rocket,
  Award,
  Shield,
  ArrowRight
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  { icon: Heart, title: 'Health & Wellness', description: 'Comprehensive medical, dental, and vision coverage' },
  { icon: Users, title: 'Work-Life Balance', description: 'Flexible schedules and remote work options' },
  { icon: Rocket, title: 'Growth Opportunities', description: 'Professional development and career advancement' },
  { icon: Award, title: 'Recognition Programs', description: 'Performance bonuses and service awards' },
  { icon: Shield, title: 'Veteran Support', description: 'Special programs for veteran employees' }
];

const values = [
  'Commitment to scientific excellence',
  'Patient-centered approach',
  'Innovation and continuous improvement',
  'Collaboration and teamwork',
  'Integrity in all we do'
];

export const Careers = ({ onDemoClick }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [formData, setFormData] = useState({
    jobs_id: '',
    name: '',
    email: '',
    phone_number: '',
    years_of_experience: '',
    current_role: '',
    preferred_location: '',
    expected_salary_range: '',
    availability: '',
    key_skills_expertise: '',
    linkedIn_profile: '',
    resume: null,
    career_goals_additiona_information: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [resumeFilename, setResumeFilename] = useState('');

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(`https://vetforcesolutions.com/backend/api/jobs`);
        console.log('Fetched jobs data:', response.data.data);
        // Ensure jobs is always an array
        const jobsData = Array.isArray(response.data.data)
          ? response.data.data 
          : response.data?.data || []; 
        setJobs(jobsData);
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setJobs([]); // Set to empty array on error
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const toggleJob = (jobId) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      await axios.post(`https://vetforcesolutions.com/backend/api/job_seekers/create`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setIsModalOpen(false);
      setFormData({
        jobs_id: '',
        name: '',
        email: '',
        phone_number: '',
        years_of_experience: '',
        current_role: '',
        preferred_location: '',
        expected_salary_range: '',
        availability: '',
        key_skills_expertise: '',
        linkedIn_profile: '',
        resume: null,
        career_goals_additiona_information: ''
      });
      setResumeFile(null);
      setResumeFilename('');
      setUploadError('');
      alert('Application submitted successfully!');
    } catch (error) {
      setSubmitError('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Upload resume file to API
  const handleFileUpload = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setResumeFile(file);
      setUploading(true);
      setUploadError("");
      try {
        const formDataUpload = new FormData();
        formDataUpload.append("post_image", file);
        const response = await fetch("https://vetforcesolutions.com/backend/api/upload_image", {
          method: "POST",
          body: formDataUpload,
        });
        const data = await response.json();
        if (data.err_code === "valid" && data.data && data.data.uploaded_pic_filename) {
          setResumeFilename(data.data.uploaded_pic_filename);
          setFormData(prev => ({ ...prev, resume: data.data.uploaded_pic_filename }));
        } else {
          setUploadError("Failed to upload resume. Please try again.");
          setResumeFilename("");
        }
      } catch (err) {
        setUploadError("Failed to upload resume. Please try again.");
        setResumeFilename("");
      } finally {
        setUploading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#090B17] pt-24" data-testid="careers-page">
      {/* Hero Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#8F262B]/5 to-transparent" />
        <div className="w-full px-6 sm:px-12 lg:px-20 relative">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-label">Careers</span>
              <h1 className="text-4xl sm:text-5xl font-outfit font-bold text-silver-shine mt-4 mb-6" data-testid="careers-title">
                Join Our <span className="text-gradient">Mission</span>
              </h1>
              <p className="text-[#C9C9CD] text-lg">
                Be part of a veteran-led team driving innovation in clinical research. 
                We're looking for passionate professionals who share our commitment to excellence.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-20 section-surface border-y border-[#2E3446]">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Our Culture</span>
              <h2 className="section-title mt-4 mb-6">
                Why Work at <span className="text-gradient">VetForce?</span>
              </h2>
              <p className="text-[#C9C9CD] mb-8">
                At VetForce Solutions, we combine military discipline with innovative thinking 
                to create a unique work environment. We value integrity, excellence, and the 
                drive to make a difference in healthcare.
              </p>

              <div className="space-y-3">
                {values.map((value, index) => (
                  <motion.div
                    key={value}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-teal-400 to-cyan-400" />
                    <span className="text-[#C9C9CD]">{value}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid sm:grid-cols-2 gap-4"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-gradient p-5"
                >
                  <div className="icon-box w-10 h-10 mb-3">
                    <benefit.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-silver-shine font-semibold mb-1">{benefit.title}</h3>
                  <p className="text-[#313031] text-sm">{benefit.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="text-center mb-12">
            <span className="section-label">Opportunities</span>
            <h2 className="section-title mt-4">Open <span className="text-gradient">Positions</span></h2>
          </div>

          {loading ? (
            <div className="space-y-4 max-w-4xl mx-auto">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card-dark p-6 animate-pulse">
                  <div className="h-6 bg-[#1B1F2C] rounded w-1/3 mb-4" />
                  <div className="h-4 bg-[#1B1F2C] rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : !Array.isArray(jobs) || jobs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#9F999F]">No open positions at this time. Check back soon!</p>
            </div>
          ) : (
            <div className="space-y-4 max-w-4xl mx-auto">
              {jobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="job-card"
                  data-testid={`job-${job.id}`}
                >
                  <div 
                    className="flex items-start justify-between cursor-pointer"
                    onClick={() => toggleJob(job.id)}
                  >
                    <div>
                      <h3 className="text-xl font-outfit font-semibold text-silver-shine mb-2">
                        {job.title} ({job.job_id})
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-[#9F999F]">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4 text-[#8F262B]" />
                          {job.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-[#8F262B]" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-[#8F262B]" />
                          {job.type}
                        </span>
                      </div>
                    </div>
                    <button className="text-[#C9C9CD] hover:text-[#8F262B] transition-colors p-2">
                      {expandedJob === job.id ? (
                        <ChevronUp className="w-6 h-6" />
                      ) : (
                        <ChevronDown className="w-6 h-6" />
                      )}
                    </button>
                  </div>

                  {expandedJob === job.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-6 pt-6 border-t border-[#2E3446]"
                    >
                        <div className="job-description" dangerouslySetInnerHTML={{ __html: job.description }} ></div>

                      <button 
                        onClick={() => {
                          setSelectedJobId(job.id);
                          setFormData(prev => ({ ...prev, jobs_id: job.id }));
                          setIsModalOpen(true);
                          setResumeFile(null);
                          setResumeFilename('');
                          setUploadError('');
                        }}
                        className="btn-gradient px-6 py-2 mt-4 inline-flex items-center gap-2"
                        data-testid={`apply-${job.id}`}
                      >
                        Apply Now
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-gradient rounded-2xl p-12 text-center"
          >
            <h2 className="section-title mb-4">
              Don't See the Right <span className="text-gradient">Fit?</span>
            </h2>
            <p className="text-[#C9C9CD] max-w-xl mx-auto mb-8">
              We're always looking for talented individuals. Send us your resume and let's connect.
            </p>
            <a 
              href="mailto:careers@vetforcesolutions.com"
              className="btn-gradient px-8 py-4 inline-flex items-center gap-2"
              data-testid="careers-email-btn"
            >
              Contact Our Team
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-[#1B1F2C] border-[#2E3446]">
          <DialogHeader>
            <DialogTitle className="text-silver-shine">Job Application</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {submitError && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm">
                {submitError}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Years of Experience</label>
                <input
                  type="number"
                  name="years_of_experience"
                  value={formData.years_of_experience}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Current Role</label>
                <input
                  type="text"
                  name="current_role"
                  value={formData.current_role}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div>
                <label className="form-label">Preferred Location</label>
                <input
                  type="text"
                  name="preferred_location"
                  value={formData.preferred_location}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Expected Salary Range</label>
                <input
                  type="text"
                  name="expected_salary_range"
                  value={formData.expected_salary_range}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="e.g., $50,000 - $70,000"
                />
              </div>
              <div>
                <label className="form-label">Availability</label>
                <select
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="">Select Availability</option>
                  <option value="immediately">Immediately</option>
                  <option value="2_weeks">2 Weeks</option>
                  <option value="1_month">1 Month</option>
                  <option value="3_months">3 Months</option>
                </select>
              </div>
            </div>

            <div>
              <label className="form-label">Key Skills & Expertise</label>
              <textarea
                name="key_skills_expertise"
                value={formData.key_skills_expertise}
                onChange={handleChange}
                rows={3}
                className="form-input resize-none"
                placeholder="List your key skills and areas of expertise..."
              />
            </div>

            <div>
              <label className="form-label">LinkedIn Profile</label>
              <input
                type="url"
                name="linkedIn_profile"
                value={formData.linkedIn_profile}
                onChange={handleChange}
                className="form-input"
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>

            <div>
              <label className="form-label">Resume</label>
              <input
                type="file"
                name="resume"
                onChange={handleFileUpload}
                className="form-input"
                accept=".pdf,.doc,.docx"
                disabled={uploading}
              />
              {uploading && <p className="text-[#C9C9CD] text-sm mt-1">Uploading...</p>}
              {uploadError && <p className="text-red-400 text-sm mt-1">{uploadError}</p>}
              {resumeFilename && <p className="text-green-400 text-sm mt-1">Resume uploaded: {resumeFilename}</p>}
            </div>

            <div>
              <label className="form-label">Career Goals & Additional Information</label>
              <textarea
                name="career_goals_additiona_information"
                value={formData.career_goals_additiona_information}
                onChange={handleChange}
                rows={4}
                className="form-input resize-none"
                placeholder="Tell us about your career goals and any additional information..."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-gradient w-full py-3 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
